import React, { FC, useContext, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';

import arrowDown from '../../static/images/arrow_down.svg';
import useOutsideClick from '../../hooks/useOutsideClick';
import { CommonContext } from '../../contexts/commonContext';
import { SUPPORTED_CURRENCIES, type CurrencyObject } from '../../helpers/currency/getProductCurrency';

import { Arrow, Wrapper, Menu, Label, ItemWrapper, Status } from './styles';

const CurrencySelector: FC = () => {
  const { t } = useTranslation();
  const { currency, setCurrency, rates, hasRatesError } = useContext(CommonContext);
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuId = useId();
  useOutsideClick(ref, () => setIsOpened(false));

  const handleSelect = (c: CurrencyObject) => {
    if (!Number.isFinite(rates[c.name]) || rates[c.name] <= 0) return;
    setCurrency(c);
    setIsOpened(false);
    buttonRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && isOpened) {
      event.preventDefault();
      setIsOpened(false);
      buttonRef.current?.focus();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (!isOpened) {
      setIsOpened(true);
      return;
    }
    const items = Array.from(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]:not(:disabled)') || []);
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 :
      currentIndex < 0 ? (event.key === 'ArrowUp' ? items.length - 1 : 0) :
      (currentIndex + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[nextIndex]?.focus();
  };

  useEffect(() => {
    if (isOpened) ref.current?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
  }, [isOpened]);

  return (
    <Wrapper
      ref={ref}
      onKeyDown={handleKeyDown}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setIsOpened(false);
      }}
      onScroll={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Label
        ref={buttonRef}
        type="button"
        aria-label={`Currency: ${currency.name}`}
        aria-haspopup="menu"
        aria-expanded={isOpened}
        aria-controls={isOpened ? menuId : undefined}
        className={isOpened ? 'isOpened' : ''}
        onClick={() => setIsOpened((value) => !value)}
      >
        {currency.name}
        <Arrow className={isOpened ? 'isOpened' : ''} src={arrowDown} alt="" aria-hidden="true" />
      </Label>
      {isOpened && (
        <Menu id={menuId} role="menu" aria-label="Currencies">
          {SUPPORTED_CURRENCIES.map((c) => (
            <ItemWrapper
              key={c.code}
              type="button"
              role="menuitemradio"
              aria-checked={currency.code === c.code}
              tabIndex={currency.code === c.code ? 0 : -1}
              disabled={!Number.isFinite(rates[c.name]) || rates[c.name] <= 0}
              className={currency.code === c.code ? 'selected' : ''}
              onClick={() => handleSelect(c)}
            >
              {c.name}
            </ItemWrapper>
          ))}
          {hasRatesError && (
            <Status role="status">
              {t('currencyRatesUnavailable', {
                defaultValue: 'Exchange rates are unavailable. Prices are shown in USD.',
              })}
            </Status>
          )}
        </Menu>
      )}
    </Wrapper>
  );
};

export default CurrencySelector;
